/**
 * 一括品切れグルーピング定義のパーサー
 *
 * テキスト形式:
 *   [グループ名]
 *     条件1
 *     条件2*       ← * 末尾で前方一致
 *     *条件3*      ← 前後に * で部分一致
 *     条件4        ← 完全一致
 */

export type MatchType = 'exact' | 'prefix' | 'suffix' | 'contains';

export interface MatchRule {
  /** 元のテキスト行（トリム済み） */
  raw: string;
  /** マッチに使うキーワード（* を除去したもの） */
  keyword: string;
  /** マッチ種別 */
  matchType: MatchType;
}

export interface SoldoutGroup {
  name: string;
  rules: MatchRule[];
}

export interface ParseResult {
  groups: SoldoutGroup[];
  errors: ParseError[];
}

export interface ParseError {
  line: number;
  message: string;
}

function detectMatchType(trimmed: string): {
  keyword: string;
  matchType: MatchType;
} {
  const startsWithStar = trimmed.startsWith('*');
  const endsWithStar = trimmed.endsWith('*');

  if (startsWithStar && endsWithStar && trimmed.length >= 2) {
    return { keyword: trimmed.slice(1, -1), matchType: 'contains' };
  }
  if (endsWithStar) {
    return { keyword: trimmed.slice(0, -1), matchType: 'prefix' };
  }
  if (startsWithStar) {
    return { keyword: trimmed.slice(1), matchType: 'suffix' };
  }
  return { keyword: trimmed, matchType: 'exact' };
}

export function parseBulkSoldoutGroupDef(text: string): ParseResult {
  const lines = text.split('\n');
  const groups: SoldoutGroup[] = [];
  const errors: ParseError[] = [];
  let currentGroup: SoldoutGroup | null = null;
  const groupNameLines = new Map<string, number>();

  for (let i = 0; i < lines.length; i++) {
    const lineNum = i + 1;
    const raw = lines[i];
    const trimmed = raw.trim();

    if (trimmed === '') continue;

    // `[` で始まるが `]` で閉じていない行はエラー
    if (trimmed.startsWith('[') && !trimmed.endsWith(']')) {
      errors.push({
        line: lineNum,
        message: 'グループヘッダの閉じ括弧 ] がありません',
      });
      continue;
    }

    const groupMatch = trimmed.match(/^\[(.*)\]$/);
    if (groupMatch) {
      const groupName = groupMatch[1].trim();
      if (groupName === '') {
        errors.push({ line: lineNum, message: 'グループ名が空です' });
        continue;
      }
      const firstLine = groupNameLines.get(groupName);
      if (firstLine !== undefined) {
        errors.push({
          line: lineNum,
          message: `グループ「${groupName}」は ${firstLine} 行目で既に定義されています`,
        });
        continue;
      }
      groupNameLines.set(groupName, lineNum);
      currentGroup = { name: groupName, rules: [] };
      groups.push(currentGroup);
      continue;
    }

    if (!currentGroup) {
      errors.push({
        line: lineNum,
        message: 'グループ宣言 [名前] の前に条件が記述されています',
      });
      continue;
    }

    const { keyword, matchType } = detectMatchType(trimmed);
    if (keyword === '') {
      errors.push({ line: lineNum, message: '条件のキーワードが空です' });
      continue;
    }

    currentGroup.rules.push({ raw: trimmed, keyword, matchType });
  }

  return { groups, errors };
}

/** ルールが商品名にマッチするかを判定 */
export function matchesRule(itemName: string, rule: MatchRule): boolean {
  switch (rule.matchType) {
    case 'exact':
      return itemName === rule.keyword;
    case 'prefix':
      return itemName.startsWith(rule.keyword);
    case 'suffix':
      return itemName.endsWith(rule.keyword);
    case 'contains':
      return itemName.includes(rule.keyword);
  }
}

/** マッチ種別の表示ラベル */
export function matchTypeLabel(matchType: MatchType): string {
  switch (matchType) {
    case 'exact':
      return '完全一致';
    case 'prefix':
      return '前方一致';
    case 'suffix':
      return '後方一致';
    case 'contains':
      return '部分一致';
  }
}

/** ルールの引っかかり例を返す */
export function matchExample(rule: MatchRule): string {
  switch (rule.matchType) {
    case 'exact':
      return `"${rule.keyword}" のみ一致`;
    case 'prefix':
      return `例: "${rule.keyword}○○"`;
    case 'suffix':
      return `例: "○○${rule.keyword}"`;
    case 'contains':
      return `例: "○○${rule.keyword}○○"`;
  }
}
